<template>
  <CModal :show="isVisible" :closeOnBackdrop="false" :centered="false" color="dark" size="xl" class="view-product-modal">
    <template #header>
      <h5 class="modal-title">View Product</h5>
      <CButton class="close" @click="productID = null">
        <span aria-hidden="true">&times;</span>
      </CButton>
    </template>

    <div v-if="loading" class="d-flex justify-content-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <div v-else>

      <div id="container">
        <div class="product-details">
          <h1>{{ item.name }}</h1>

          <div class="d-flex flex-column">
            <div class="detail">
              <span class="title">#ID</span>
              <span class="content">{{ item.id }}</span>
            </div>
            <div class="detail">
              <span class="title">GTIN</span>
              <span class="content">{{ item.gtin }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('firm.firm_name') }}</span>
              <span class="content">{{ getValue('firm', 'name') }}</span>
            </div>
            <div class="detail">
              <span class="title">Brand</span>
              <span class="content">{{ getValue('brand', 'name') }}</span>
            </div>

            <div class="detail">
              <span class="title">{{ $t('product.default_category') }}</span>
              <span class="content">{{ getValue('default_category', 'code') }} {{ getValue('default_category', 'name') }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.unit_net_amount') }}</span>
              <span class="content" v-if="item.net_content">
                {{ item.net_content }}
                {{ item.net_content_unit ? item.net_content_unit.name : item.net_content_unit_code }}
              </span>
              <span class="content" v-else>{{ nullChar }}</span>
            </div>
          </div>
        </div>

        <div class="product-image text-align">
          <div v-show="loadingImg" class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <img v-show="!loadingImg" :src="productImage" @load="onImgLoad">

          <div class="info">
            <div class="detail">
              <span class="title">{{ $t('product.ingredient') }}</span>
              <span class="content">{{ item.feature.ingredient }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.alcohol_content') }}</span>
              <span class="content">{{ item.feature.alcohol_content }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.storage_instructions') }}</span>
              <span class="content">{{ item.feature.storage_instructions }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.allergen_information') }}</span>
              <span class="content">{{ item.feature.allergen_information }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.age_restriction') }}</span>
              <span class="content">{{ item.feature.age_restricted }}</span>
            </div>
          </div>
        </div>
      </div>

      <CTabs class="tabs" :active-tab="0" variant="tabs" :justified="true">
        <CTab>
          <template slot="title">
            <CIcon class="align-bottom" height="18" name="cil-star" /> {{ $t('product.features') }}
          </template>

          <div class="product-features d-flex flex-column">
            <div class="detail">
              <span class="title">{{ $t('product.fbo') }}</span>
              <span class="content">{{ item.feature.fbo }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.fbo_address') }}</span>
              <span class="content">{{ item.feature.fbo_address }}</span>
            </div>

            <hr>

            <div class="detail">
              <span class="title">{{ $t('product.ingredient') }}</span>
              <span class="content">{{ item.feature.ingredient }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.alcohol_content') }}</span>
              <span class="content">{{ item.feature.alcohol_content }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.storage_instructions') }}</span>
              <span class="content">{{ item.feature.storage_instructions }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.allergen_information') }}</span>
              <span class="content">{{ item.feature.allergen_information }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.age_restriction') }}</span>
              <span class="content">{{ item.feature.age_restricted }}</span>
            </div>

            <hr>

            <div class="detail">
              <span class="title">{{ $t('product.vegetarian') }}</span>
              <span class="content">{{ $t(`global.${item.feature.vegetarian ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.vegan') }}</span>
              <span class="content">{{ $t(`global.${item.feature.vegan ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.lactose_free') }}</span>
              <span class="content">{{ $t(`global.${item.feature.lactose_free ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.gluten_free') }}</span>
              <span class="content">{{ $t(`global.${item.feature.gluten_free ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.bio_mark') }}</span>
              <span class="content">{{ $t(`global.${item.feature.bio_mark ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.safe_in_pregnancy') }}</span>
              <span class="content">{{ $t(`global.${item.feature.safe_in_pregnancy ? 'yes' : 'no'}`) }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.safe_for_drive') }}</span>
              <span class="content">{{ $t(`global.${item.feature.safe_for_drive ? 'yes' : 'no'}`) }}</span>
            </div>

            <hr />

            <div class="detail">
              <span class="title">{{ $t('product.energy_cal') }}</span>
              <span class="content">{{ item.feature.energy_cal }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.energy_j') }}</span>
              <span class="content">{{ item.feature.energy_j }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.sugars') }}</span>
              <span class="content">{{ item.feature.sugars }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.salt_equivalent') }}</span>
              <span class="content">{{ item.feature.salt_equivalent }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.fat') }}</span>
              <span class="content">{{ item.feature.fat }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.saturated_fat') }}</span>
              <span class="content">{{ item.feature.saturated_fat }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.protein') }}</span>
              <span class="content">{{ item.feature.protein }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.carbohydrate') }}</span>
              <span class="content">{{ item.feature.carbohydrate }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.fibre') }}</span>
              <span class="content">{{ item.feature.fibre }}</span>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.co2') }}</span>
              <span class="content">{{ item.feature.co2 }}</span>
            </div>
          </div>
        </CTab>

        <CTab v-if="item.detail">
          <template slot="title">
            <CIcon class="align-bottom" height="18" name="cil-list" /> {{ $t('global.details') }}
          </template>

          <div class="product-detail d-flex flex-column">
            <div class="detail">
              <span class="title">{{ $t('product.alcohol_content') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.alcohol"
                  :value="item.detail.alcohol"
                  :expand-depth=2
                  boxed
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.allergen_information') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.allergen"
                  :value="item.detail.allergen"
                  :expand-depth=2
                  boxed
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.compulsory_additive_label') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.compulsory_additive_label"
                  :value="item.detail.compulsory_additive_label"
                  :expand-depth=2
                  boxed
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.storage_instructions') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.consumer_storage_instruction"
                  :value="item.detail.consumer_storage_instruction"
                  :expand-depth=2
                  boxed
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.consumer_usage_instruction') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.consumer_usage_instruction"
                  :value="item.detail.consumer_usage_instruction"
                  :expand-depth=2
                  boxed
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.diet_information') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.diet_information"
                  :value="item.detail.diet_information"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.ingredients.ingredients') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.ingredients"
                  :value="item.detail.ingredients"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.lifespan') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.lifespan"
                  :value="item.detail.lifespan"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.measurement') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.measurement"
                  :value="item.detail.measurement"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.nutrient_details') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.nutrient_details"
                  :value="item.detail.nutrient_details"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.nutritional_claim') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.nutritional_claim"
                  :value="item.detail.nutritional_claim"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.packaging_information') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.packaging_information"
                  :value="item.detail.packaging_information"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.sales_information') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.sales_information"
                  :value="item.detail.sales_information"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.temperature') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.temperature"
                  :value="item.detail.temperature"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.trade_item_feature_benefit') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.trade_item_feature_benefit"
                  :value="item.detail.trade_item_feature_benefit"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>
            <div class="detail">
              <span class="title">{{ $t('product.trade_item_marketing_message') }}</span>
              <div class="content">
                <json-viewer
                  v-if="item.detail.trade_item_marketing_message"
                  :value="item.detail.trade_item_marketing_message"
                  :expand-depth=2
                  boxed
                  :show-array-index="false"
                >
                </json-viewer>
              </div>
            </div>

          </div>
        </CTab>
      </CTabs>
    </div>

    <template #footer>
      <CButton @click="productID = null" color="link">{{ $t('global.cancel') }}</CButton>
    </template>

  </CModal>
</template>

<script>
import JsonViewer from 'vue-json-viewer'

export default {
  name: "ViewProductModal",
  components: {
    JsonViewer
  },
  props: {
    id: { default: null, type: [Number, String, null] },
  },
  data() {
    return {
      item: null,
      loading: true,
      loadingImg: true,
    }
  },

  computed: {
    nullChar: { get() { return process.env.VUE_APP_NULL_CHAR } },
    isVisible() {
      return this.id !== null && this.id !== undefined;
    },
    productID: {
      get() {
        return this.id;
      },
      set(newValue) {
        this.$emit("update:id", newValue);
      },
    },
    productImage() {
      return this.item && this.item.cover_image_url ? this.item.cover_image_url : '../../../img/no-product-image.png';
    },
  },

  async mounted() {
    if (this.isVisible) {
      this.$nextTick(() => {
        const modalDialog = document.getElementsByClassName("modal-dialog")[0];
        if (modalDialog) {
          modalDialog.classList.add("modal-dialog-scrollable", "h-100");
        }
      });
      
      await this.getItem();
    }
  },
  
  watch: {
    id(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.$nextTick(() => {
          const modalDialog = document.getElementsByClassName("modal-dialog")[0];
          if (modalDialog) {
            modalDialog.classList.add("modal-dialog-scrollable", "h-100");
          }
        });
        this.getItem();
      }
    }
  },

  methods: {
    getItem: async function () {
      if (!this.productID) return;
      
      this.loading = true;
      this.loadingImg = true;
      
      var url = new URL(this.$backend.get('PRODUCT', 'VIEW', this.productID));

      await this.$axios
        .get(url)
        .then((response) => {
          this.item = response.data || {};
          if (!this.item.feature) {
            this.$set(this.item, 'feature', {});
          }
        })
        .catch((error) => { console.log(error); })
        .finally(() => {
          this.loading = false;
        });
    },

    getValue: function (...args) {
      if (args.length < 2) return '';

      return this.item[args[0]] ? this.item[args[0]][args[1]] : '';
    },

    onImgLoad () {
      this.loadingImg = false;
    }
  }

};
</script>

<style scoped>
.tab-pane {
  margin: 1rem;
}
</style>

<style>
.detail {
  display: flex;
  flex-direction: row;
  margin-bottom: 5px;
}
.detail .title {
  min-width: 150px;
  width: 150px;
  font-weight: bold;
}
.detail .content {
  width: 100%;
}

.product-features .detail .title {
  min-width: 240px;
  width: 240px;
}

.info .detail {
  flex-direction: column;
  margin-bottom: 18px;
}
.info .detail .title {
  min-width: 100%;
  width: 100%;
}

.product-detail .detail {
  flex-direction: column;
  margin-bottom: 12px;
}
.product-detail .detail .title {
  min-width: 100%;
  width: 100%;
}

@media only screen and (max-width: 767px) {
  .detail {
    flex-direction: column;
  }
  .detail .title {
    min-width: 100%;
    width: 100%;
  }
}

@media only screen and (min-width: 768px) {
  .detail .title:not(.info .detail .title, .product-detail .detail .title)::after {
    content: ":";
    float: right;
    margin-right: 5px;
  }
}

hr {
  width: 100%;
  border: 0;
  height: 1px;
  background-image: linear-gradient(to right, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
}

/* --------------------------------------------------------------------------------------------- */

#container {
  /* box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px; */
  box-shadow: rgba(0, 0, 0, 0.05) 0px 0px 0px 1px;
  text-align: center;
  border-radius: 0;
  overflow: hidden;
  margin: 0 auto 1rem;
  height: 100%;
  min-height: 350px;
  width: 100%;
}

.product-details {
  position: relative;
  text-align: left;
  overflow: hidden;
  padding: 30px;
  height: 100%;
  float: left;
  width: 70%;
}

.product-details h1 {
  display: inline-block;
  position: relative;
  font-size: 30px;
  color: #344055;
  margin-bottom: 1rem;
}

.product-details>p {
  font-size: 18px;
  color: #7d7d7d;
}

.product-image {
  transition: all 0.3s ease-out;
  display: inline-block;
  position: relative;
  overflow: hidden;
  display: inline-block;
  margin: auto;

  height: 350px;
  width: 30%;
  object-fit: contain;

  /* Vertically center a div element for all browsers */
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-justify-content: center;
  -ms-flex-pack: center;
  justify-content: center;
  -webkit-align-items: center;
  -ms-flex-align: center;
  align-items: center;
}

.product-image img {
  max-width: 100%;
  max-height: 95%;
}

.info {
  background: rgba(27, 26, 26, 0.6);
  transition: all 0.3s ease-out;
  transform: translateX(-100%);
  position: absolute;
  text-align: left;
  color: #FFF;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;

  padding: 10px;
  font-size: 14px;
}

.product-image:hover .info {
  transform: translateX(0);
}

.product-image:hover img {
  transition: all 0.3s ease-out;
}

.product-image:hover img {
  transform: scale(1.2, 1.2);
}

/* --------------------------------------------------------------------------------------------- */
/* json-viewer */

.jv-container .jv-code {
  padding: 10px !important;
}
</style>
